import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import DeliveryPartnerInfo from '@shopDetails/components/DeliveryPartnerInfo';
import { getDeliveryAmount } from '@shopDetails/utils/plus';
import {
  Container,
  LinkReviews,
  Rating,
  Count,
  Separator,
  Tags,
} from './components';
import PlusTag from '../PlusTag/index';

const ShopInformation = ({
  shop,
  reviewsRoute,
  isDesktop,
  isPickup,
  discountsTotalAmount,
  plusSettings,
  shippingAmount,
  isPickupFlagEnabled,
  isLogisticsDelivery,
  weeklyDiscount,
  pickupDiscount,
}) => {
  const showReviews = useMemo(
    () => shop?.acceptsReviews && shop?.validReviewCount > 0,
    [shop],
  );
  const discount = useMemo(() => discountsTotalAmount || shop?.discount, [
    discountsTotalAmount,
    shop,
  ]);
  const isFreeShipping = useMemo(() => shippingAmount === 0, [shippingAmount]);
  const showDeliveryTag = useMemo(
    () => !(isPickupFlagEnabled && isLogisticsDelivery) || isPickup,
    [isLogisticsDelivery, isPickupFlagEnabled, isPickup],
  );
  const showPlusTag = useMemo(
    () => Boolean(isDesktop) && Boolean(plusSettings) && !isFreeShipping,
    [isDesktop, plusSettings, isFreeShipping],
  );
  const plusDeliveryAmount = useMemo(
    () => getDeliveryAmount(shippingAmount, plusSettings),
    [shippingAmount, plusSettings],
  );

  const showSeparator = useMemo(() => {
    if (isPickupFlagEnabled) {
      return !!discount;
    }
    return !!discount || showDeliveryTag || showPlusTag;
  }, [discount, showDeliveryTag, showPlusTag, isPickupFlagEnabled]);

  const shouldHideShopInfo =
    !showReviews && !discount && !showDeliveryTag && !showPlusTag;

  if (shouldHideShopInfo) return null;

  return (
    <Container>
      {Boolean(showReviews) && (
        <>
          <LinkReviews to={reviewsRoute}>
            <Rating score={shop?.generalScore} />
            <Count isDesktop={isDesktop}>({shop?.validReviewCount})</Count>
          </LinkReviews>
          {showSeparator && <Separator />}
        </>
      )}
      <Tags>
        {showDeliveryTag && (
          <DeliveryPartnerInfo
            weeklyDiscount={weeklyDiscount}
            pickupDiscount={pickupDiscount}
          />
        )}
        {showPlusTag && (
          <Container>
            <PlusTag
              desktopMode={isDesktop}
              isPickup={isPickup}
              finalPricePlus={plusDeliveryAmount}
              originalPricePlus={shippingAmount}
            />
          </Container>
        )}
      </Tags>
    </Container>
  );
};

ShopInformation.propTypes = {
  shop: PropTypes.shape({
    discount: PropTypes.number,
    generalScore: PropTypes.number,
    validReviewCount: PropTypes.number,
    acceptsReviews: PropTypes.bool,
  }).isRequired,
  reviewsRoute: PropTypes.string.isRequired,
  isDesktop: PropTypes.bool,
  isPickup: PropTypes.bool,
  discountsTotalAmount: PropTypes.number,
  shippingAmount: PropTypes.number,
  plusSettings: PropTypes.object,
  isPickupFlagEnabled: PropTypes.bool,
  isLogisticsDelivery: PropTypes.bool,
  weeklyDiscount: PropTypes.object,
  pickupDiscount: PropTypes.object,
};

export default ShopInformation;
